import React, { useState } from "react";

const DISCORD_WEBHOOK_URL =
  "https://discord.com/api/webhooks/1375104089477615698/JvzDVhnOWw3Wjq6poTOJZbKXK-jCfDNFA34BLfOCZWc1mBLi2FuQdrrZ7c3mRvoATbKA";

export default function AppealMinorPunishment() {
  const [formData, setFormData] = useState({
    username: "",
    punishmentType: "",
    punishmentDate: "",
    punishmentReason: "",
    appealExplanation: "",
    evidenceURL: "",
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);

  const punishmentOptions = ["Warning", "Mute", "Kick"];

  const validate = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!formData.punishmentType) newErrors.punishmentType = "Select a punishment type";
    if (!formData.punishmentDate) newErrors.punishmentDate = "Date is required";
    if (!formData.punishmentReason.trim()) newErrors.punishmentReason = "Please state the reason given";
    if (!formData.appealExplanation.trim()) newErrors.appealExplanation = "Please explain your appeal";

    if (formData.evidenceURL.trim()) {
      const urlRegex =
        /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
      if (!urlRegex.test(formData.evidenceURL.trim()))
        newErrors.evidenceURL = "Enter a valid URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    setStatus(null); // Clear status on input change for better UX
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setStatus(null);
      return;
    }

    setStatus("Submitting...");

    const embed = {
      title: "🚨 Appeal: Warning / Mute / Kick",
      color: 0x00d8d6,
      fields: [
        { name: "Username", value: formData.username, inline: true },
        { name: "Punishment Type", value: formData.punishmentType, inline: true },
        { name: "Punishment Date", value: formData.punishmentDate, inline: true },
        { name: "Reason Given", value: formData.punishmentReason },
        { name: "Appeal Explanation", value: formData.appealExplanation },
      ],
      timestamp: new Date().toISOString(),
    };

    if (formData.evidenceURL.trim()) {
      embed.fields.push({ name: "Evidence URL", value: formData.evidenceURL.trim() });
    }

    try {
      const res = await fetch(DISCORD_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ embeds: [embed] }),
      });

      if (res.ok) {
        setStatus("✅ Appeal submitted successfully!");
        setFormData({
          username: "",
          punishmentType: "",
          punishmentDate: "",
          punishmentReason: "",
          appealExplanation: "",
          evidenceURL: "",
        });
      } else {
        setStatus("⚠️ Submission failed. Please try again.");
      }
    } catch {
      setStatus("⚠️ Network error. Please check your connection.");
    }
  };

  return (
    <div
      style={{
        maxWidth: 540,
        margin: "3rem auto",
        background:
          "radial-gradient(circle at top left, #09203f 0%, #537895 100%)",
        borderRadius: 20,
        padding: "3rem 2.5rem",
        boxShadow: "0 14px 28px rgba(0, 216, 214, 0.6), 0 10px 10px rgba(0,0,0,0.2)",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#e0f7fa",
        userSelect: "none",
        transition: "all 0.3s ease",
      }}
      aria-label="Appeal minor punishment form"
    >
      <h2
        style={{
          fontSize: "2.8rem",
          marginBottom: "1.5rem",
          background:
            "linear-gradient(90deg, #00e5ff, #00b0ff, #0091ea)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontWeight: "900",
          letterSpacing: 2,
          textTransform: "uppercase",
          textAlign: "center",
          textShadow: "0 0 12px #00bcd4",
        }}
      >
        Appeal Minor Punishment
      </h2>

      <form onSubmit={handleSubmit} noValidate>
        {[
          {
            label: "Your Username",
            name: "username",
            type: "text",
            placeholder: "Minecraft username",
            required: true,
            value: formData.username,
            error: errors.username,
          },
          {
            label: "Punishment Type",
            name: "punishmentType",
            type: "select",
            options: punishmentOptions,
            required: true,
            value: formData.punishmentType,
            error: errors.punishmentType,
          },
          {
            label: "Date of Punishment",
            name: "punishmentDate",
            type: "date",
            required: true,
            value: formData.punishmentDate,
            error: errors.punishmentDate,
          },
          {
            label: "Reason Given for Punishment",
            name: "punishmentReason",
            type: "textarea",
            placeholder: "What was the reason?",
            required: true,
            value: formData.punishmentReason,
            error: errors.punishmentReason,
          },
          {
            label: "Your Appeal Explanation",
            name: "appealExplanation",
            type: "textarea",
            placeholder: "Why should your punishment be lifted?",
            required: true,
            value: formData.appealExplanation,
            error: errors.appealExplanation,
          },
          {
            label: "Evidence URL (optional)",
            name: "evidenceURL",
            type: "url",
            placeholder: "Link to proof (Imgur, YouTube, etc.)",
            required: false,
            value: formData.evidenceURL,
            error: errors.evidenceURL,
          },
        ].map(({ label, name, type, placeholder, options, required, value, error }) => (
          <div key={name} style={{ marginBottom: 24, position: "relative" }}>
            <label
              htmlFor={name}
              style={{
                display: "block",
                fontWeight: "700",
                marginBottom: 8,
                color: error ? "#ff5252" : "#80deea",
                userSelect: "none",
                fontSize: "1rem",
                letterSpacing: 0.5,
              }}
            >
              {label} {required && <span style={{ color: "#ff5252" }}>*</span>}
            </label>

            {type === "select" ? (
              <select
                id={name}
                name={name}
                value={value}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem 1.2rem",
                  borderRadius: 16,
                  border: error ? "2.5px solid #ff5252" : "2.5px solid #00bcd4",
                  background: "#07303b",
                  color: "#a7ffff",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "border-color 0.3s ease",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg fill='%23a7ffff' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                  backgroundSize: "1rem",
                }}
                aria-invalid={!!error}
                aria-describedby={error ? `${name}-error` : undefined}
              >
                <option value="">-- Select --</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : type === "textarea" ? (
              <textarea
                id={name}
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                rows={4}
                style={{
                  width: "100%",
                  padding: "1rem 1.2rem",
                  borderRadius: 16,
                  border: error ? "2.5px solid #ff5252" : "2.5px solid #00bcd4",
                  background: "#07303b",
                  color: "#a7ffff",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  resize: "vertical",
                  transition: "border-color 0.3s ease",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                }}
                aria-invalid={!!error}
                aria-describedby={error ? `${name}-error` : undefined}
              />
            ) : (
              <input
                id={name}
                name={name}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem 1.2rem",
                  borderRadius: 16,
                  border: error ? "2.5px solid #ff5252" : "2.5px solid #00bcd4",
                  background: "#07303b",
                  color: "#a7ffff",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  transition: "border-color 0.3s ease",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                }}
                aria-invalid={!!error}
                aria-describedby={error ? `${name}-error` : undefined}
              />
            )}

            {error && (
              <small
                id={`${name}-error`}
                style={{
                  color: "#ff8a80",
                  fontSize: "0.85rem",
                  position: "absolute",
                  bottom: -20,
                  left: 0,
                  fontWeight: "600",
                  userSelect: "text",
                }}
              >
                {error}
              </small>
            )}
          </div>
        ))}

        <button
          type="submit"
          style={{
            width: "100%",
            background:
              "linear-gradient(90deg, #00e5ff, #00b0ff, #0091ea)",
            border: "none",
            padding: "1rem 0",
            fontSize: "1.3rem",
            fontWeight: "900",
            borderRadius: 24,
            color: "#003c58",
            cursor: "pointer",
            userSelect: "none",
            boxShadow: "0 10px 25px rgba(0, 188, 212, 0.75)",
            transition: "background 0.35s ease, transform 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(90deg, #0091ea, #00b0ff, #00e5ff)";
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(90deg, #00e5ff, #00b0ff, #0091ea)";
            e.currentTarget.style.transform = "scale(1)";
          }}
          aria-live="polite"
          aria-busy={status === "Submitting..."}
          disabled={status === "Submitting..."}
        >
          {status === "Submitting..." ? "Submitting..." : "Submit Appeal"}
        </button>

        {status && (
          <p
            role="alert"
            style={{
              marginTop: 22,
              textAlign: "center",
              fontWeight: "700",
              color: status.includes("success") ? "#80deea" : "#ff8a80",
              userSelect: "none",
              minHeight: 26,
              fontSize: "1rem",
              textShadow: "0 0 4px rgba(0,0,0,0.3)",
              transition: "opacity 0.3s ease",
            }}
          >
            {status}
          </p>
        )}
      </form>
    </div>
  );
}
